import { importExcel } from "./excel-import";
import type { AuditMetrics, AuditProgress, AuditResult, CompletedAudit } from "./types";

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

function applyDuplicateReview(results: AuditResult[]): AuditResult[] {
  const counts = new Map<string, number>();
  results.forEach((item) => {
    const text = window.CallAudit.normalize(item.record.comment);
    if (text.length >= 35) counts.set(text, (counts.get(text) || 0) + 1);
  });
  return results.map((item) => {
    const text = window.CallAudit.normalize(item.record.comment);
    const count = counts.get(text) || 0;
    if (count < 2 || item.decision !== "compliant") return item;
    return {
      ...item,
      decision: "manual_review",
      code: "DUPLICATE_TEMPLATE",
      reason: `Одинаковый подробный комментарий повторяется ${count} раз; требуется ручная проверка.`,
      signals: [...item.signals, `повтор комментария: ${count}`],
      confidence: 75,
    };
  });
}

export function summarize(results: AuditResult[]): AuditMetrics {
  return {
    total: results.length,
    checked: results.filter((item) => item.decision !== "skip").length,
    violations: results.filter((item) => item.decision === "violation").length,
    manual: results.filter((item) => item.decision === "manual_review").length,
    compliant: results.filter((item) => item.decision === "compliant").length,
  };
}

export async function runAudit(
  file: File,
  onProgress: (progress: AuditProgress) => void,
): Promise<CompletedAudit> {
  const startedAt = performance.now();
  onProgress({ percent: 3, label: "Читаем структуру книги", processed: 0, total: 0 });
  const imported = await importExcel(file);
  const total = imported.records.length;
  onProgress({ percent: 12, label: `Найдено событий: ${total.toLocaleString("ru-RU")}`, processed: 0, total });
  await nextFrame();

  const preliminary: AuditResult[] = [];
  const batchSize = total > 10_000 ? 600 : 300;
  for (let start = 0; start < total; start += batchSize) {
    const batch = imported.records.slice(start, start + batchSize);
    preliminary.push(...window.CallAudit.auditBatch(batch));
    const processed = Math.min(start + batch.length, total);
    onProgress({
      percent: 12 + (processed / total) * 78,
      label: `Проверено ${processed.toLocaleString("ru-RU")} из ${total.toLocaleString("ru-RU")}`,
      processed,
      total,
    });
    await nextFrame();
  }

  onProgress({ percent: 94, label: "Проверяем повторяющиеся комментарии", processed: total, total });
  const results = applyDuplicateReview(preliminary);
  await nextFrame();
  const durationMs = performance.now() - startedAt;
  onProgress({ percent: 100, label: "Контрольный отчёт готов", processed: total, total });
  return { ...imported, sourceName: file.name, results, metrics: summarize(results), durationMs };
}
